import {take, fork, select, put} from 'redux-saga/effects';
import {efServiceCaseTypes, efServiceAssignmentTypes, efServiceAssignmentStatusTypes} from '@ace-de/eua-entity-types';
import fetchRequest from '../../application/sagas/fetchRequest';
import updateServiceCase from '../../service-cases/sagas/updateServiceCase';
import updateServiceAssignment from './updateServiceAssignment';
import * as serviceCaseHelpers from '../../service-cases/sagas/serviceCaseHelpers';
import * as serviceAssignmentActionTypes from '../serviceAssignmentActionTypes';

/**
 * Update service assignment damage location flow
 */
const updateServiceAssignmentDamageLocationFlow = function* updateServiceAssignmentDamageLocationFlow() {
    const {serviceManager} = yield select(state => state.application);
    const googleMapsService = serviceManager.loadService('googleMapsService');

    while (true) {
        const {payload} = yield take(serviceAssignmentActionTypes.SUBMIT_SERVICE_ASSIGNMENT_DAMAGE_LOCATION_FORM);
        const {serviceCaseId, serviceAssignmentLineNo, serviceCaseData, serviceAssignmentData} = payload;

        const serviceAssignmentId = `${serviceCaseId}-${serviceAssignmentLineNo}`;
        const serviceAssignment = yield select(state => {
            return state.serviceAssignments.serviceAssignments[serviceAssignmentId];
        });
        yield* serviceCaseHelpers.setPersistencePending(serviceCaseId);

        const serviceCase = yield select(state => state.serviceCases.serviceCases[serviceCaseId]);
        if (serviceCase.member?.personalDetails?.coordinates) {
            const memberResidenceLocation = serviceCase.member.personalDetails.coordinates;
            const damageLocation = serviceCaseData.damage.location;

            yield fork(
                fetchRequest,
                serviceAssignmentActionTypes.FETCH_RESIDENCE_TO_DAMAGE_LOCATION_DISTANCE_REQUEST,
                googleMapsService.getRoute,
                {
                    origin: {
                        location: {
                            latLng: {
                                latitude: damageLocation?.latitude,
                                longitude: damageLocation?.longitude,
                            },
                        },
                    },
                    destination: {
                        location: {
                            latLng: {
                                latitude: memberResidenceLocation?.latitude,
                                longitude: memberResidenceLocation?.longitude,
                            },
                        },
                    },
                    fields: ['routes.distanceMeters'],
                },
            );

            const routeResponseAction = yield take([
                serviceAssignmentActionTypes.FETCH_RESIDENCE_TO_DAMAGE_LOCATION_DISTANCE_REQUEST_SUCCEEDED,
                serviceAssignmentActionTypes.FETCH_RESIDENCE_TO_DAMAGE_LOCATION_DISTANCE_REQUEST_FAILED,
            ]);

            if (!routeResponseAction.error) {
                const {response} = routeResponseAction.payload;
                const {routes} = response;
                serviceCaseData['distanceResidenceToDamageLocation'] = routes[0]?.distanceMeters
                    ? routes[0].distanceMeters / 1000 : 0;
            }
        }

        // We have to patch serviceAssignment.serviceLocation whenever agent set serviceCase.damage.location
        // From the ECS web app perspective, we will never read this field, but it is mandatory for the driver app,
        // and they need to read it from the assignment
        yield* updateServiceAssignment({
            caller: serviceAssignmentActionTypes.SUBMIT_SERVICE_ASSIGNMENT_DAMAGE_LOCATION_FORM,
            assignmentType: efServiceAssignmentTypes.VEHICLE,
            serviceAssignmentLineNo,
            serviceCaseId,
            serviceAssignmentData: {
                ...serviceAssignmentData,
                ...(serviceAssignment?.assignmentStatus === efServiceAssignmentStatusTypes.NEW
                    ? {towingDestination: null, acePartner: null, assignmentText: null} : {}),
            },
        });

        yield* updateServiceCase({
            caller: serviceAssignmentActionTypes.SUBMIT_SERVICE_ASSIGNMENT_DAMAGE_LOCATION_FORM,
            caseType: efServiceCaseTypes.VEHICLE,
            serviceCaseId,
            serviceCaseData,
        });

        // reset towing destination recommendations and recommended towing destination on damage location change
        // reset contract partner recommendations and recommended contract partner on damage location change
        if (serviceAssignment?.assignmentStatus === efServiceAssignmentStatusTypes.NEW) {
            yield put({
                type: serviceAssignmentActionTypes.SET_TOWING_DESTINATION_RECOMMENDATIONS,
                payload: {
                    serviceAssignmentId,
                    towingDestinationRecommendationDTOs: [],
                    recommendedTowingDestinationId: null,
                },
            });

            yield put({
                type: serviceAssignmentActionTypes.SET_CONTRACT_PARTNER_RECOMMENDATIONS,
                payload: {
                    serviceAssignmentId,
                    contractPartnerRecommendationDTOs: [],
                    recommendedContractPartnerId: null,
                },
            });
        }
    }
};

export default updateServiceAssignmentDamageLocationFlow;
